import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useComplianceCalendar } from "../../hooks/useComplianceCalendar";
import { useComplianceTypes } from "../../hooks/useComplianceTypes";
import { ComplianceTypeCard } from "../../components/ComplianceTypeCard";
import { CalendarEntryForm } from "../../components/CalendarEntryForm";
import { MasterComplianceCalendarEntity } from "../../../domain/entities/MasterComplianceCalendarEntity";
import { Button } from "../../../../../ui/components/Button";
import { FeatherSettings } from "@subframe/core";

export const ComplianceCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    groupedEntries,
    loading,
    error,
    fetchGroupedEntries,
    createCalendarEntry,
    updateCalendarEntry,
    deleteCalendarEntry,
  } = useComplianceCalendar();
  const { complianceTypes, fetchComplianceTypes } = useComplianceTypes();

  useEffect(() => {
    fetchComplianceTypes();
  }, [fetchComplianceTypes]);
  const [selectedComplianceTypeId, setSelectedComplianceTypeId] = useState<
    number | null
  >(null);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] =
    useState<MasterComplianceCalendarEntity | null>(null);
  const [deletingEntryId, setDeletingEntryId] = useState<number | null>(null);

  useEffect(() => {
    fetchGroupedEntries();
    // Check for typeId in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const typeId = urlParams.get("typeId");
    if (typeId) {
      setSelectedComplianceTypeId(parseInt(typeId));
    }
  }, [fetchGroupedEntries]);

  const handleAddEntry = (complianceTypeId: number) => {
    setSelectedComplianceTypeId(complianceTypeId);
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditEntry = (entry: MasterComplianceCalendarEntity) => {
    setSelectedComplianceTypeId(entry.compliance_type_id);
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDeleteEntry = async (entryId: number) => {
    try {
      setDeletingEntryId(entryId);
      await deleteCalendarEntry(entryId);
    } catch (err: any) {
      alert(err.message || "Failed to delete calendar entry");
    } finally {
      setDeletingEntryId(null);
    }
  };

  const handleFormSubmit = async (
    formData: Partial<MasterComplianceCalendarEntity>,
  ) => {
    try {
      if (editingEntry) {
        await updateCalendarEntry(editingEntry.id, formData);
      } else {
        await createCalendarEntry({
          ...formData,
          compliance_type_id: selectedComplianceTypeId!,
        });
      }
      setShowForm(false);
      setEditingEntry(null);
      setSelectedComplianceTypeId(null);
    } catch (err: any) {
      alert(err.message || "Failed to save calendar entry");
    }
  };

  return (
    <div className="flex h-screen w-full flex-col bg-default-background">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading calendar entries...</p>
          </div>
        </div>
      )}
      <div className="flex w-full flex-col items-start gap-6 p-12">
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Compliance Calendar
              </span>
              <span className="text-body font-body text-subtext-color">
                Manage compliance calendar entries grouped by compliance type
              </span>
            </div>
          </div>
          <Button
            variant="neutral-primary"
            icon={<FeatherSettings />}
            onClick={() => navigate("/admin/compliance/types")}
          >
            Manage Compliance Types
          </Button>
        </div>

        {error && (
          <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-800">
              {error.message}
            </span>
          </div>
        )}

        {/* Grouped Calendar Entries */}
        <div className="flex w-full flex-col items-start gap-4">
          {Object.keys(groupedEntries).map((typeId) => {
            const group = groupedEntries[typeId];
            return (
              <ComplianceTypeCard
                key={typeId}
                type={group.type}
                entries={group.entries}
                onAddEntry={handleAddEntry}
                onEditEntry={handleEditEntry}
                onDeleteEntry={handleDeleteEntry}
                deletingEntryId={deletingEntryId}
              />
            );
          })}

          {Object.keys(groupedEntries).length === 0 && !loading && (
            <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
              No compliance types found. Please create compliance types first.
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <h3 className="text-heading-3 font-heading-3 mb-4">
              {editingEntry ? "Edit Calendar Entry" : "Add Calendar Entry"}
            </h3>
            <CalendarEntryForm
              complianceTypes={complianceTypes}
              initialData={editingEntry}
              selectedComplianceTypeId={selectedComplianceTypeId}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingEntry(null);
                setSelectedComplianceTypeId(null);
              }}
              loading={loading}
            />
          </div>
        </div>
      )}
    </div>
  );
};
